import React from 'react';
import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, createBrowserHistory, initAsyncRouting } from '@thd-olt-component-react/router';
import { LayoutManagerProvider } from '@thd-olt-component-react/layout-manager';
import { RedirectProvider } from '@thd-nucleus/redirector';
import { CustomerProvider } from '@thd-olt-functional/customer-information';
import { ThemeProvider } from '@thd-olt-component-react/theme-provider';
import { getHeadersForAPI } from '@thd-nucleus/request-manager/client';
import { HelmetProvider as ThdHelmetProvider } from '@thd-nucleus/thd-helmet';
import { ContextBuilder, ExperienceProvider } from '@thd-nucleus/experience-context';
import { DataProviderClient } from '@thd-nucleus/data-sources';
import { getServerProps } from './server-to-client';
import { AppProvider } from './Providers/AppProvider';

import { withBypassComponent } from './withBypassComponent';

class ClientContext {

  constructor({ mockLink, opts = {} } = {}) {
    const history = createBrowserHistory();
    const serverData = getServerProps();
    const { headers, projectConfig, opts: serverOpts } = serverData;
    this.opts = {
      disableLocalization: false,
      ...serverOpts,
      ...opts
    };
    this.headers = {
      ...headers,
      ...getHeadersForAPI()
    };
    const { name, prerender, useMock } = serverData.config;

    this.name = name;
    this.serverData = serverData;
    this.disableSSR = window.DISABLE_SSR;
    const { experienceContext } = ContextBuilder.client();
    this.expContext = {
      experienceContext
    };
    this.apollo = {
      useMock,
      link: useMock && mockLink ? mockLink : null
    };
    this.expConfiguration = this.serverData.expConfiguration;
    this.thdSEOBotDetection = this.serverData.thdSEOBotDetection;
    this.layoutManagerRules = {};

    this.theme = {
      name: serverData.themeName
    };

    this.redirect = {
      ...serverData.redirect
    };

    this.customer = this.serverData.customer;

    this.router = {
      history,
      currentRouteName: serverData.currentRouteName,
      enabled: true
    };

    const productPodHoverDelay = parseInt(this.expConfiguration['spa.product-pod-hover-delay'], 10);
    this.featureSwitches = {
      apolloFederation: typeof this.opts.federation !== 'undefined'
        ? this.opts.federation
        : this.expConfiguration['apollo.federation'] === 'true',
      dynamicBotRendering: this.expConfiguration['fs-dynamic-bot-rendering'] === 'true',
      disableCrossExpCheck: this.expConfiguration['spa.disable-cross-exp-check'] === 'true',
      prefetchPip: this.expConfiguration['spa.prefetch-pip'] || 'both',
      productPodHoverDelay: Number.isNaN(productPodHoverDelay) ? 500 : productPodHoverDelay
    };
    if (this.opts.component) {
      this.disableRouter();
    }
  }

  disableRouter() {
    this.router.enabled = false;
  }

  disableLocalization() {
    this.opts.disableLocalization = true;
  }

  setExperienceRoutes(routes) {
    this.router.routes = routes;
  }

  setHeader(key, value) {
    this.headers[key] = value;
  }

  setLayoutManagerRules(rules) {
    this.layoutManagerRules = rules;
  }

  setStore(store) {
    this.expContext.experienceContext.store = store;
  }

  setClientStore(store) {
    this.expContext.experienceContext.clientStore = store;
  }

  getRouterConfig() {
    const routingConfig = {
      currentRouteName: this.router.currentRouteName,
      routes: this.router.routes,
      asyncChunks: this.serverData.asyncChunks,
      channel: this.expContext.experienceContext.channel
    };

    return routingConfig;
  }

  setTheme(opts, templateDep) {
    let name;
    let template;
    if (typeof opts === 'string') {
      // eslint-disable-next-line max-len
      console.log('warning, setTheme takes a object with {name, template}. calling setTheme(name, template) is deprecated');
      name = opts;
      template = templateDep;
    } else if (typeof opts === 'object') {
      name = opts.name;
      template = opts.template;
    }

    if (name !== this.theme.name) {
      console.log(`Warning: theme name does not match server: (Server: ${this.theme.name} / Client: ${name} `);
    } else {
      this.theme.template = template;
    }

  }

  async render(App) {

    if (!this.opts.component && !/homedepot(dev)?\.com/.test(window.location.hostname)) {
      return null;
    }

    const render = this.disableSSR
      ? ReactDOM.render
      : ReactDOM.hydrate;

    const Router = withBypassComponent(BrowserRouter, this.router.enabled);
    const Redirect = withBypassComponent(RedirectProvider, this.router.enabled);

    const node = document.getElementById('root');

    const thdHelmetContext = {};

    if (this.router.enabled) {
      this.router.currentRouteComponent = await initAsyncRouting(this.getRouterConfig());
    }

    if (Object.keys(this.layoutManagerRules).length === 0) {
      console.warn('No layout manager rules have been specified!');
    }

    return render(
      <AppProvider instance={this}>
        <CustomerProvider>
          <ExperienceProvider
            value={this.expContext.experienceContext}
            disableLocalization={this.opts.disableLocalization}
          >
            <LayoutManagerProvider featureSwitch={this.expConfiguration} context={this.layoutManagerRules}>
              <HelmetProvider>
                <ThdHelmetProvider context={thdHelmetContext}>
                  <Router
                    history={this.router.history}
                    experience={this.name}
                    disableCrossExpCheck={this.featureSwitches.disableCrossExpCheck}
                  >
                    <DataProviderClient
                      debug={window.isDebugMode}
                      link={this.apollo.link}
                      federation={this.featureSwitches.apolloFederation}
                      headers={this.headers}
                      addTypename={!this.apollo.useMock}
                    >
                      <ThdHelmetProvider context={thdHelmetContext}>
                        <Redirect
                          history={this.router.history}
                          bypass={this.redirect.bypassRedirect}
                          pathWithQueryParams={this.redirect.path}
                          itemId={this.redirect.itemId}
                          packageId={this.redirect.packageId}
                          navParam={this.redirect.navparam}
                          keyword={this.redirect.keyword}
                          nttKeyword={this.redirect.nett}
                          timeoutRecovery
                        >
                          <ThemeProvider theme={this.theme.template}>
                            {App}
                          </ThemeProvider>
                        </Redirect>
                      </ThdHelmetProvider>
                    </DataProviderClient>
                  </Router>
                </ThdHelmetProvider>
              </HelmetProvider>
            </LayoutManagerProvider>
          </ExperienceProvider>
        </CustomerProvider>
      </AppProvider>,
      node
    );
  }
}

export { ClientContext };
